/**
 * Coalesced, account-scoped scheduler for recovering the visible conversation
 * list when realtime events reference a conversation not yet loaded in Vuex.
 *
 * Trailing debounce: bursts of message.created collapse into one refresh after
 * the quiet window. Account/generation changes cancel pending work so a stale
 * timer cannot fire after the agent switches account or filters are torn down.
 */

export const VISIBLE_LIST_REFRESH_DEBOUNCE_MS = 1000;

let timer = null;
let scheduledAccountId = null;
let generation = 0;

export const resetVisibleListRefreshState = () => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  scheduledAccountId = null;
  generation += 1;
};

export const cancelVisibleListRefresh = () => {
  resetVisibleListRefreshState();
};

/**
 * @param {object} options
 * @param {number|string|null} options.accountId
 * @param {() => void} options.run
 * @param {number} [options.debounceMs]
 */
export const scheduleVisibleListRefresh = ({
  accountId,
  run,
  debounceMs = VISIBLE_LIST_REFRESH_DEBOUNCE_MS,
}) => {
  if (accountId == null || typeof run !== 'function') return;

  if (scheduledAccountId !== null && scheduledAccountId !== accountId) {
    resetVisibleListRefreshState();
  }

  scheduledAccountId = accountId;

  if (timer) {
    clearTimeout(timer);
    timer = null;
  }

  const scheduledGeneration = generation;
  const capturedAccountId = accountId;

  timer = setTimeout(() => {
    timer = null;
    if (scheduledGeneration !== generation) return;
    if (scheduledAccountId !== capturedAccountId) return;
    run();
  }, debounceMs);
};

export const getVisibleListRefreshDebugState = () => ({
  hasTimer: Boolean(timer),
  scheduledAccountId,
  generation,
});
